import classnames from 'classnames'
import { useCallback } from 'react'

import Product from '@src/quince-site-common/src/domains/Product'
import Bundle from '@src/quince-site-common/src/domains/Product/BundleProduct'
import Icon from '../icon'
import ProductReviewHighLight, { ProductReviewHighLightProps } from '../productReviewHighlight'

import Star from '../../assets/star.inline.svg'

import useExperimentBucket from '@src/quince-site-common/src/hooks/useExperimentBucket'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'

import { showReviewSection } from './reviewHighLightsSection.helpers'

import { EXPERIMENTS } from '@src/experiments'

import styles from './reviewHighLightsSection.module.scss'

export interface ReviewHighLightsSectionProps {
  header?: string
  highLights: ProductReviewHighLightProps[]
  className?: string
  product: Product | Bundle
}

const ReviewHighLightsSection = ({ highLights = [], ...props }: ReviewHighLightsSectionProps) => {
  const classes = classnames(styles.container, props.className)
  const size = highLights.length
  const showAllReviews = size > 3 ? true : false
  const arr = size > 3 ? highLights.slice(0, 3) : highLights

  const getIsVisibleTrackParams = useCallback(() => {
    return {
      action: 'pdp_accordion_reviews',
      value: true,
      context: {
        productId: props.product.shopifyProductId,
        productName: props.product.title,
      },
    }
  }, [props.product.id])

  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(getIsVisibleTrackParams)

  const averageRating = props.product.productReviewSummary?.averageRating
  const reviewCount = props.product.productReviewSummary?.reviewCount

  const experimentBucket = useExperimentBucket(EXPERIMENTS.REVIEW_HIGHLIGHTS_MOBILE.experimentName)
  const isHighlightSummaryVisible = experimentBucket === EXPERIMENTS.REVIEW_HIGHLIGHTS_MOBILE.bucketNames.show

  return (
    <div ref={trackIsVisibleRef} className={classes}>
      <div className={styles.container__wrapper}>
        <div
          className={classnames(styles.container__wrapper__header, {
            [styles.container__wrapper__header__withoutSummary]: !isHighlightSummaryVisible,
            [styles.container__wrapper__header__withSummary]: isHighlightSummaryVisible,
          })}
        >
          {props.header || 'REVIEW HIGHLIGHTS'}
          {isHighlightSummaryVisible && (
            <div className={styles.reviewHighlightDetails}>
              <Icon iconSvg={Star} className={styles.reviewHighlightDetails__starIcon} />
              <span>{averageRating}</span>
              <span className={styles.reviewHighlightDetails__separator}>|</span>
              <span
                role="button"
                tabIndex={0}
                onClick={showReviewSection}
                className={styles.reviewHighlightDetails__reviewCountLink}
              >
                {reviewCount} REVIEWS
              </span>
            </div>
          )}
        </div>
        {arr.map((highLight, index) => {
          return (
            <ProductReviewHighLight key={index} {...highLight} className={styles.container__wrapper__reviewHighLight} />
          )
        })}
        {showAllReviews && (
          <div className={styles.container__wrapper__all} onClick={showReviewSection}>
            READ ALL REVIEWS
          </div>
        )}
      </div>
    </div>
  )
}

export default ReviewHighLightsSection
